import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Burger, Navbar as MNavbar, NavLink as MNavLink, useMantineTheme, Box, ScrollArea,
  createStyles, Stack, Divider, Anchor,
} from '@mantine/core';
import {
  TablerIcon,
} from '@tabler/icons';
import { useViewportSize } from '@mantine/hooks';

import { ProjectSelector } from '../project-selector';
import { grp, getRootRoute } from '../../routes';
import { useActiveRoute } from '../../hooks/use-active-route';
import { AppContext } from '../../contexts/app';
import { PROJECT_NAME } from '../../constants';
import { mainMenu, footerMenu } from './menu';
import type { MenuItem } from './menu';

import Logo from '../../assets/images/logo.svg';
import LogoWhite from '../../assets/images/logo-white.svg';

const useStyles = createStyles((theme, _params, getRef) => ({
  // getref styles api'de olmadığı için link hover state'i burada yazıldı
  linkIcon: {
    ref: getRef('icon'),
  },
  link: {
    [`&:hover .${getRef('icon')}`]: {
      color: theme.colorScheme === 'dark' ? theme.white : theme.colors.brand[6],
    },
  },
}));

type NavLinkProps = {
  icon: TablerIcon;
  label: string;
  active: boolean;
  link: string;
  setNavbarOpened: (value: React.SetStateAction<boolean>) => void;
};

const NavLink: React.FC<NavLinkProps> = ({
  icon: Icon, label, active, link, setNavbarOpened,
}) => {
  const { classes } = useStyles();

  return (
    <MNavLink
      key={link}
      component={Link}
      to={link}
      active={active}
      icon={<Icon className={classes.linkIcon} size={20} stroke={2} />}
      label={label}
      fw={500}
      px="xs"
      py={8}
      className={classes.link}
      onClick={() => { setNavbarOpened(false); }}
      styles={(_theme) => ({
        root: {
          borderRadius: _theme.radius.md,
          '&[data-active]': {
            backgroundColor: _theme.colorScheme === 'dark' ? _theme.colors.dark[5] : '#E3EAFD',
            '&:hover': {
              backgroundColor: _theme.colorScheme === 'dark' ? _theme.colors.dark[5] : '#E3EAFD',
            },
          },
          '&:hover': {
            backgroundColor: 'initial',
            color: _theme.colorScheme === 'dark' ? _theme.white : _theme.colors.brand[9],
          },
        },
        label: {
          fontSize: 14,
        },
      })}
    />
  );
};

export const Navbar: React.FC<{
  navbarOpened: boolean
  setNavbarOpened: (value: React.SetStateAction<boolean>) => void
}> = ({ navbarOpened, setNavbarOpened }) => {
  const theme = useMantineTheme();
  const location = useLocation();
  const { pathname } = location;
  const { appState } = useContext(AppContext);
  const route = useActiveRoute();
  const rootRoute = route ? getRootRoute(route.id) : null;
  const { height: viewportHeight } = useViewportSize();

  const seperatorBorder = `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`;

  const isActive = (item: MenuItem) => {
    if (item.checkPath) {
      return item.link === pathname;
    }
    return rootRoute?.id === item.routeId;
  };

  const mainLinks = mainMenu
    .filter((i) => {
      if (i.onlyForProjectOwner) {
        return appState.currentProjectRole === 'owner';
      }
      return true;
    })
    .map((item) => (
      <NavLink
        key={item.link}
        icon={item.icon}
        label={item.label}
        active={isActive(item)}
        link={item.link}
        setNavbarOpened={setNavbarOpened}
      />
    ));

  const footerLinks = footerMenu.map((item) => (
    <NavLink
      key={item.link}
      icon={item.icon}
      label={item.label}
      active={isActive(item)}
      link={item.link}
      setNavbarOpened={setNavbarOpened}
    />
  ));

  return (
    <MNavbar
      hiddenBreakpoint="md"
      hidden={!navbarOpened}
      py="sm"
      px="md"
      bg={theme.colorScheme === 'dark' ? theme.colors.dark[6] : '#F5F8FD'}
      width={{ md: 260 }}
      mih={viewportHeight}
    >
      <MNavbar.Section
        pb="md"
        mb={theme.spacing.md * 1.5}
        sx={{
          borderBottom: seperatorBorder,
          [theme.fn.largerThan('md')]: {
            borderBottomColor: 'transparent',
          },
        }}
      >
        <Burger
          opened={navbarOpened}
          onClick={() => setNavbarOpened((o) => !o)}
          size="sm"
          py="lg"
          color={theme.colors.gray[6]}
          display={{ base: 'block', md: 'none' }}
        />

        <Box pt="sm" px="sm" display={{ base: 'none', md: 'inline-block' }}>
          <Box h={24}>
            <Anchor component={Link} to={grp('homepage')}>
              {theme.colorScheme === 'dark'
                ? <img src={LogoWhite} height={24} alt={PROJECT_NAME} />
                : <img src={Logo} height={24} alt={PROJECT_NAME} />}
            </Anchor>
          </Box>
        </Box>
      </MNavbar.Section>

      <MNavbar.Section
        grow
        component={ScrollArea}
        sx={{
          marginLeft: -theme.spacing.md,
          marginRight: -theme.spacing.md,
        }}
      >
        <Box px="md">
          {mainLinks}
        </Box>
      </MNavbar.Section>

      <MNavbar.Section mt="md">
        <Stack spacing="xs">
          <Box>
            {footerLinks}
          </Box>

          <Divider
            my={0}
            color={theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}
          />

          <Box>
            <ProjectSelector setNavbarOpened={setNavbarOpened} />
          </Box>
        </Stack>
      </MNavbar.Section>
    </MNavbar>
  );
};
